import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import LandingShell from '@/components/landing/LandingShell';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { 
  ArrowLeft, 
  Calendar, 
  Clock, 
  User, 
  Share2, 
  Bookmark,
  Heart,
  MessageSquare,
  Newspaper,
  Send
} from 'lucide-react';
import { NewsItem } from '@/types';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import SEO from '@/components/common/SEO';
import { Card, CardContent } from '@/components/ui/card';
import { formatContentForDisplay } from '@/utils/htmlUtils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
};

const formatTime = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit' 
  });
};

const shareArticle = () => {
  if (navigator.share) {
    navigator.share({
      title: 'Share this article',
      url: window.location.href
    });
  } else {
    navigator.clipboard.writeText(window.location.href);
    alert('Link copied to clipboard!');
  }
};

interface Comment {
  id: string;
  author: string;
  content: string;
  date: string;
}

const NewsDetail = () => {
  const { slug: newsId } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [newsItem, setNewsItem] = useState<NewsItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [relatedNews, setRelatedNews] = useState<NewsItem[]>([]);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState<number>(0);
  const [likeLoading, setLikeLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  // Real comments state
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentText, setCommentText] = useState('');
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [postingComment, setPostingComment] = useState(false);

  // Fetch news item from Supabase
  useEffect(() => {
    let isMounted = true;
    async function fetchNewsItem() {
      if (!newsId) {
        setNewsItem(null);
        setLoading(false);
        return;
      }

      if (newsItem && newsItem.id === newsId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setNewsItem(null);

      const { data, error } = await supabase
        .from('news')
        .select('*')
        .eq('id', newsId)
        .single();
      
      if (!isMounted) return;

      if (error) {
        setNewsItem(null);
        toast({
          title: "Error loading article",
          description: error.message,
          variant: "destructive"
        });
      } else if (!data) {
        setNewsItem(null);
      } else {
        setNewsItem(data);
        setLikeCount(data.likes || 0);
      }
      setLoading(false);
    }
    fetchNewsItem();
    return () => { isMounted = false; };
  }, [newsId, toast, newsItem]);

  // Fetch related articles from Supabase
  useEffect(() => {
    let isMounted = true;
    async function fetchRelatedNews() {
      if (!newsId) {
        setRelatedNews([]);
        return;
      }
      const { data, error } = await supabase
        .from('news')
        .select('*')
        .neq('id', newsId)
        .order('date', { ascending: false })
        .limit(3);
      if (!error && Array.isArray(data)) {
        if (isMounted) setRelatedNews(data);
      } else {
        console.error("Error fetching related news:", error);
        if (isMounted) setRelatedNews([]);
      }
    }
    fetchRelatedNews();
    return () => { isMounted = false; };
  }, [newsId]);

  // Fetch comments from Supabase
  const fetchComments = async () => {
    if (!newsId) return;
    setCommentsLoading(true);
    try {
      const { data, error } = await supabase
        .from('news_comments')
        .select('*')
        .eq('news_id', newsId)
        .order('date', { ascending: false });
      if (error) throw error;
      setComments(
        (data || []).map((c: any) => ({
          id: c.id,
          author: c.author,
          content: c.content,
          date: c.date
        }))
      );
    } catch (err) {
      console.error("Error fetching comments:", err);
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  };

  // Fetch comments on mount and when newsId changes
  useEffect(() => {
    fetchComments();
    if (!newsId) return;
    const channel = supabase
      .channel('realtime-news-comments')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'news_comments', filter: `news_id=eq.${newsId}` },
        () => {
          fetchComments();
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line
  }, [newsId]);

  // --- Like logic ---
  useEffect(() => {
    const fetchLikeStatus = async () => {
      if (!user || !newsId) {
        setIsLiked(false);
        return;
      }
      const { data } = await supabase
        .from('news_likes')
        .select('id')
        .eq('news_id', newsId)
        .eq('user_id', user.id)
        .maybeSingle();
      setIsLiked(!!data);
    };
    fetchLikeStatus();
  }, [user, newsId]);

  useEffect(() => {
    if (!newsId) return;
    const channel = supabase
      .channel('realtime-news-likes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'news_likes', filter: `news_id=eq.${newsId}` },
        () => {
          supabase
            .from('news')
            .select('likes')
            .eq('id', newsId)
            .single()
            .then(({ data }) => {
              setLikeCount(data?.likes || 0);
            });
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [newsId]);

  const handleLike = async () => {
    if (!user) {
      toast({
        title: "Login required",
        description: "You must be logged in to like this article.",
        variant: "destructive"
      });
      return;
    }
    if (!newsId) return;
    setLikeLoading(true);
    try {
      if (!isLiked) {
        const { error: likeError } = await supabase
          .from('news_likes')
          .insert({ news_id: newsId, user_id: user.id });
        if (likeError && !likeError.message.includes('duplicate')) throw likeError;
        await supabase.rpc('increment_news_likes', { news_id_input: newsId });
        setIsLiked(true);
        setLikeCount((prev) => prev + 1);
      } else {
        await supabase
          .from('news_likes')
          .delete()
          .eq('news_id', newsId)
          .eq('user_id', user.id);
        await supabase.rpc('decrement_news_likes', { news_id_input: newsId });
        setIsLiked(false);
        setLikeCount((prev) => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error("Error updating like status:", err);
      toast({
        title: "Error",
        description: "Could not update like status.",
        variant: "destructive"
      });
    } finally {
      setLikeLoading(false);
    }
  };

  const handleCommentSubmit = async () => {
    if (!user) {
      toast({
        title: "Login required",
        description: "You must be logged in to post a comment.",
        variant: "destructive"
      });
      return;
    }
    if (!commentText.trim()) {
      toast({
        title: "Comment cannot be empty",
        description: "Please write something before submitting.",
        variant: "destructive"
      });
      return;
    }
    setPostingComment(true);
    try {
      const { error } = await supabase.from('news_comments').insert({
        news_id: newsId,
        author: user.user_metadata?.full_name || user.email || "User",
        content: commentText.trim(),
        date: new Date().toISOString()
      });
      if (error) throw error;
      setCommentText('');
      toast({
        title: "Comment added",
        description: "Your comment has been posted successfully!",
      });
      fetchComments();
    } catch (err) {
      console.error("Failed to post comment:", err);
      toast({
        title: "Failed to post comment",
        description: "There was an error posting your comment.",
        variant: "destructive"
      });
    } finally {
      setPostingComment(false);
    }
  };

  const formatCommentDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffTime / (1000 * 60));
    
    if (diffDays > 0) {
      return diffDays === 1 ? 'Yesterday' : `${diffDays} days ago`;
    } else if (diffHours > 0) {
      return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes} ${diffMinutes === 1 ? 'minute' : 'minutes'} ago`;
    } else {
      return 'Just now';
    }
  };

  if (loading) {
    return (
      <LandingShell>
        <div className="flex items-center justify-center py-24">
          <div className="animate-pulse flex flex-col space-y-4 w-full max-w-3xl p-4">
            <div className="h-6 bg-muted/50 rounded w-3/4"></div>
            <div className="h-80 bg-muted/30 rounded"></div>
            <div className="space-y-2">
              <div className="h-4 bg-muted/50 rounded w-5/6"></div>
              <div className="h-4 bg-muted/50 rounded w-full"></div>
              <div className="h-4 bg-muted/50 rounded w-5/6"></div>
              <div className="h-4 bg-muted/50 rounded w-3/4"></div>
            </div>
          </div>
        </div>
      </LandingShell>
    );
  }

  if (!newsItem) {
    return (
      <LandingShell>
        <SEO
          title="Article Not Found"
          description="The news article you are looking for could not be found."
        />
        <div className="container py-16 px-4 max-w-3xl mx-auto text-center">
          <Newspaper className="mx-auto h-16 w-16 text-muted-foreground opacity-30" />
          <h1 className="text-2xl font-bold mt-4">Article Not Found</h1>
          <p className="mt-2 text-muted-foreground">
            Sorry, we couldn't find the article you're looking for.
          </p>
          <Button 
            className="mt-8" 
            variant="default" 
            onClick={() => navigate('/news')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to News
          </Button>
        </div>
      </LandingShell>
    );
  }

  const category = newsItem.category || 'General';
  const formattedDate = formatDate(newsItem.date);
  const formattedTime = formatTime(newsItem.date);
  
  return (
    <LandingShell>
      <SEO
        title={newsItem.title}
        description={newsItem.content.substring(0, 160) + '...'}
        imageUrl={newsItem.image_url || undefined}
        url={`/news/${newsId}`}
      />
      <div className="relative">
        {newsItem.image_url && (
          <div className="relative h-[50vh] min-h-[400px] w-full bg-gray-900">
            <div 
              className="absolute inset-0 bg-cover bg-center z-0 opacity-60"
              style={{ backgroundImage: `url(${newsItem.image_url})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent z-10" />
          </div>
        )}
        
        <div className="relative z-20 container mx-auto px-4 sm:px-6 max-w-5xl">
          <div className={`${newsItem.image_url ? '-mt-48' : 'pt-20'} pb-16`}>
            <div className="flex items-center gap-4 mb-8">
              <Button 
                variant="outline" 
                size="sm" 
                className="flex items-center gap-2 bg-background/80 backdrop-blur-sm border-muted hover:text-black dark:text-white hover:bg-background/90" 
                onClick={() => navigate('/news')}
              >
                <ArrowLeft className="h-4 w-4" />
                Back to News
              </Button>
              <Badge 
                className="py-1.5 px-3 text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 border-none"
              >
                {category}
              </Badge>
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-6">{newsItem.title}</h1>
            
            <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground mb-10">
              <div className="flex items-center gap-2">
                <User size={14} />
                <span>{newsItem.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar size={14} />
                <span>{formattedDate}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock size={14} />
                <span>{formattedTime}</span>
              </div>
            </div>
            
            <div className="flex justify-between items-center mb-10">
              <div className="flex items-center gap-2">
                <Button 
                  variant={isLiked ? "default" : "outline"} 
                  size="sm" 
                  className={`rounded-full transition-colors ${isLiked ? 'bg-primary hover:bg-primary/90 text-primary-foreground' : ''}`}
                  onClick={handleLike}
                  disabled={likeLoading}
                >
                  <Heart className={`mr-2 h-4 w-4 ${isLiked ? 'fill-white text-white' : ''}`} />
                  {isLiked ? "Liked" : "Like"}
                  <span className="ml-2">{likeCount}</span>
                </Button>
                <Button 
                  variant={isBookmarked ? "default" : "outline"} 
                  size="sm" 
                  className={`rounded-full transition-colors ${isBookmarked ? 'bg-primary hover:bg-primary/90 text-primary-foreground' : ''}`}
                  onClick={() => setIsBookmarked(!isBookmarked)}
                >
                  <Bookmark className={`mr-2 h-4 w-4 ${isBookmarked ? 'fill-current' : ''}`} />
                  Save
                </Button>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="rounded-full" 
                onClick={shareArticle}
              >
                <Share2 className="mr-2 h-4 w-4" />
                Share
              </Button>
            </div>
            
            {newsItem.image_url && !newsItem.is_main_featured_article && (
              <div className="mb-10 rounded-lg overflow-hidden shadow-lg">
                <img 
                  src={newsItem.image_url} 
                  alt={newsItem.title} 
                  className="w-full h-auto"
                />
              </div>
            )}
            
            <div className="prose prose-lg dark:prose-invert rich-text-content">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
              >
                {newsItem.content}
              </ReactMarkdown>
            </div>
            
            <div className="mt-16 p-6 border rounded-lg bg-card/50">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <User className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">{newsItem.author}</h3>
                  <p className="text-sm text-muted-foreground">Admin</p>
                </div>
              </div>
            </div>
            
            <div className="mt-16">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">Comments</h2>
                <span className="text-muted-foreground">{comments.length} comments</span>
              </div>
              
              <div className="space-y-6">
                {commentsLoading ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageSquare className="mx-auto h-10 w-10 opacity-30 mb-2" />
                    <p>Loading comments...</p>
                  </div>
                ) : comments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageSquare className="mx-auto h-10 w-10 opacity-30 mb-2" />
                    <p>No comments yet. Be the first to share your thoughts!</p>
                  </div>
                ) : (
                  comments.map((comment) => (
                    <div key={comment.id} className="p-4 border rounded-lg">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="h-10 w-10 rounded-full bg-secondary/30 flex items-center justify-center">
                          <User className="h-5 w-5" />
                        </div>
                        <div>
                          <h4 className="font-medium">{comment.author}</h4>
                          <p className="text-xs text-muted-foreground">{formatCommentDate(comment.date)}</p>
                        </div>
                      </div>
                      <p className="text-sm">{comment.content}</p>
                    </div>
                  ))
                )}
              </div>
              
              <div className="mt-8">
                <h3 className="font-medium mb-3">Leave a comment</h3>
                <div className="relative">
                  <textarea 
                    className="w-full p-3 border rounded-md bg-background min-h-[100px]" 
                    placeholder="Write your comment here..."
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    disabled={postingComment}
                  />
                  <Button 
                    className="mt-3"
                    onClick={handleCommentSubmit}
                    disabled={postingComment || !commentText.trim()}
                  >
                    <Send className="mr-2 h-4 w-4" />
                    {postingComment ? "Posting..." : "Submit"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {relatedNews.length > 0 && (
          <div className="border-t py-16 bg-muted/10">
            <div className="container mx-auto px-4 max-w-6xl">
              <h2 className="text-2xl font-bold mb-8">Related Articles</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {relatedNews.map((news) => (
                  <Card key={news.id} className="overflow-hidden border-0 shadow hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/news/${news.id}`)} >
                    {news.image_url && (
                      <div className="relative">
                        <Badge className="absolute top-3 left-3 z-10">
                          {news.category || 'General'}
                        </Badge>
                        <img 
                          src={news.image_url} 
                          alt={news.title} 
                          className="w-full aspect-video object-cover"
                        />
                      </div>
                    )}
                    <CardContent className="p-4">
                      <h3 className="font-bold text-lg mb-2 hover:text-primary transition-colors line-clamp-2">
                        {news.title}
                      </h3>
                      <div className="text-sm text-muted-foreground line-clamp-2">
                        <div dangerouslySetInnerHTML={{ __html: formatContentForDisplay(news.content.split('\n')[0]) }} />
                      </div>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t text-xs text-muted-foreground">
                        <span>{formatDate(news.date)}</span>
                        <span>{news.author.split(',')[0]}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </LandingShell>
  );
};

export default NewsDetail;